import type { ICellRendererParams } from 'ag-grid-community';

/**
 * AG Grid cell renderer for cells that contain objects or arrays.
 *
 * Why: SQL drivers return JSONB/JSON columns as JavaScript objects;
 * the MongoDB driver returns every document field that's nested.
 * Stringifying inline (`JSON.stringify(value)`) makes the grid
 * unreadable — long blobs in a single row cell. Show a compact chip
 * (`{3 fields}` / `[7 items]`) so the user can spot at a glance which
 * cells hold structure, then double-click to expand in a viewer.
 *
 * Falls back to the default text path for primitives so non-JSON
 * cells keep their existing rendering.
 *
 * Pass an `onOpen(value, title)` callback through `cellRendererParams`
 * so the host grid can pop its own `JsonViewerModal`. The renderer
 * itself doesn't own modal state — too much grid-cell rerendering to
 * tie React state to.
 */
interface JsonChipParams extends ICellRendererParams {
  onOpen?: (value: unknown, title: string) => void;
}

export function JsonChipRenderer(params: JsonChipParams): JSX.Element | string {
  const v = params.value;

  if (v === null || v === undefined) {
    return <span className="text-text-muted italic">NULL</span>;
  }

  // stopPropagation on the chip's dblclick prevents AG Grid from also
  // firing onCellDoubleClicked, which would open the host grid's text-
  // based CellEditor on top of the JSON viewer — and worse, on commit
  // the editor would stringify the array/object back to text and
  // register it as a phantom pending change.
  if (Array.isArray(v)) {
    return (
      <button
        type="button"
        onDoubleClick={(e) => {
          e.stopPropagation();
          params.onOpen?.(v, String(params.colDef?.field ?? 'array'));
        }}
        className="px-1.5 py-0.5 text-2xs font-mono text-accent-blue bg-accent-blue/10 rounded-sm hover:bg-accent-blue/20 transition-colors"
        title="Double-click to expand"
      >
        [{v.length} item{v.length === 1 ? '' : 's'}]
      </button>
    );
  }

  if (typeof v === 'object') {
    const keys = Object.keys(v);
    return (
      <button
        type="button"
        onDoubleClick={(e) => {
          e.stopPropagation();
          params.onOpen?.(v, String(params.colDef?.field ?? 'object'));
        }}
        className="px-1.5 py-0.5 text-2xs font-mono text-accent-purple bg-accent-purple/10 rounded-sm hover:bg-accent-purple/20 transition-colors"
        title="Double-click to expand"
      >
        {`{${keys.length} field${keys.length === 1 ? '' : 's'}}`}
      </button>
    );
  }

  return String(v);
}
